/**
 * Pure logic behind the quest board — grouping, progress maths, reset countdowns.
 *
 * Kept out of `quests.ts` so it can be tested without a DOM: everything here is a
 * function of the payload `GET /api/progression/quests` already returns (the full
 * Prisma `UserQuest` row with its `quest` relation included) plus the clock. No
 * field is invented; anything the API does not send is simply not rendered.
 */
import type { IconName } from '../../ui/icons';

/**
 * The rows the endpoint really sends. `game-api.ts` types only the subset the old
 * list used, but the controller returns the whole record, so the extra fields are
 * declared optional here rather than widening the shared type (that file belongs
 * to another surface) — and every consumer below tolerates them being absent.
 */
export interface QuestRow {
  id: string;
  progress: number;
  status: string;
  assignedAt?: string;
  completedAt?: string | null;
  claimedAt?: string | null;
  quest: {
    slug: string;
    name: string;
    description: string;
    questType: string;
    targetCount: number;
    rewardSoft: number;
    rewardXp: number;
    action?: string;
    rewardPremium?: number;
  };
}

export const isClaimable = (q: QuestRow): boolean => q.status === 'COMPLETED';
export const isClaimed = (q: QuestRow): boolean => q.status === 'CLAIMED';

/** Completion as 0–100, clamped. A zero target would divide by zero on bad data. */
export function progressPct(progress: number, target: number): number {
  if (!(target > 0)) return progress > 0 ? 100 : 0;
  return Math.max(0, Math.min(100, Math.round((progress / target) * 100)));
}

/** Progress can overshoot the target (trackAction adds in batches) — never show 12/10. */
export const cappedProgress = (q: QuestRow): number => Math.min(q.progress, q.quest.targetCount);

export interface QuestSummary {
  total: number;
  done: number;        // completed OR already claimed — "finished today"
  claimable: number;
  coinsWaiting: number;
  xpWaiting: number;
}

/** Header numbers: how far through the board you are and what is banked but unclaimed. */
export function summarise(quests: QuestRow[]): QuestSummary {
  let done = 0, claimable = 0, coinsWaiting = 0, xpWaiting = 0;
  for (const q of quests) {
    if (isClaimable(q) || isClaimed(q)) done++;
    if (isClaimable(q)) {
      claimable++;
      coinsWaiting += q.quest.rewardSoft;
      xpWaiting += q.quest.rewardXp;
    }
  }
  return { total: quests.length, done, claimable, coinsWaiting, xpWaiting };
}

/**
 * Board order inside a group: what you can act on first, then what you are closest
 * to finishing, then the dead rows. Ties break on name so repaints never shuffle.
 */
export function sortQuests(quests: QuestRow[]): QuestRow[] {
  const rank = (q: QuestRow) => (isClaimable(q) ? 0 : isClaimed(q) ? 2 : 1);
  return [...quests].sort((a, b) =>
    rank(a) - rank(b)
    || progressPct(b.progress, b.quest.targetCount) - progressPct(a.progress, a.quest.targetCount)
    || a.quest.name.localeCompare(b.quest.name));
}

export interface QuestGroup {
  type: string;          // raw questType from the API
  label: string;
  quests: QuestRow[];
}

const GROUP_LABEL: Record<string, string> = { DAILY: 'Daily errands', WEEKLY: 'Weekly undertakings' };
const GROUP_ORDER = ['DAILY', 'WEEKLY'];

/**
 * Group by `questType`. Unknown types (a future quest kind added server-side)
 * still get a group, appended after the known ones with their raw name shown —
 * better than silently dropping quests the player has been assigned.
 */
export function groupQuests(quests: QuestRow[]): QuestGroup[] {
  const by = new Map<string, QuestRow[]>();
  for (const q of quests) {
    const t = q.quest.questType;
    (by.get(t) ?? by.set(t, []).get(t)!).push(q);
  }
  const known = GROUP_ORDER.filter((t) => by.has(t));
  const extra = [...by.keys()].filter((t) => !GROUP_ORDER.includes(t)).sort();
  return [...known, ...extra].map((t) => ({
    type: t,
    label: GROUP_LABEL[t] ?? t.charAt(0) + t.slice(1).toLowerCase(),
    quests: sortQuests(by.get(t)!),
  }));
}

// ─── Resets ─────────────────────────────────────────────────────────────────
// The backend resets quests on BullMQ cron: dailies at 00:00 UTC, weeklies at
// Monday 00:00 UTC (core/cron). Those two schedules are the only truth we have
// about expiry — `UserQuest` stores no expiry column — so the countdown is
// computed from them rather than from a field that does not exist.

/** ms until the next daily reset (next 00:00 UTC). */
export function msUntilDailyReset(now: number): number {
  const d = new Date(now);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1) - now;
}

/** ms until the next weekly reset (next Monday 00:00 UTC; exactly 7 days if it is Monday 00:00). */
export function msUntilWeeklyReset(now: number): number {
  const d = new Date(now);
  const daysToMonday = ((8 - d.getUTCDay()) % 7) || 7;
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + daysToMonday) - now;
}

/** ms until the given quest type resets, or null when we have no schedule for it. */
export function msUntilReset(questType: string, now: number): number | null {
  if (questType === 'DAILY') return msUntilDailyReset(now);
  if (questType === 'WEEKLY') return msUntilWeeklyReset(now);
  return null;
}

/**
 * Coarse countdown: the largest two units, so the label never jitters at a width
 * the layout cannot hold. `dom.fmtRemaining` is m:ss — useless for a 6-day timer.
 */
export function fmtCountdown(ms: number): string {
  if (ms <= 0) return 'resetting…';
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60), sec = s % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

// ─── Difficulty ─────────────────────────────────────────────────────────────
/**
 * Difficulty is not a column — the backend has no tier field. It is derived from
 * what the quest pays, which IS the designer's own difficulty dial in
 * `progression.config.ts` (a 60-crop weekly pays 600, a 8-crop daily pays 60).
 * Coins + 5×XP puts the two reward axes on one scale; thresholds are read off the
 * shipped quest table so each band is actually populated.
 */
export type Difficulty = 'easy' | 'steady' | 'hard' | 'epic';

export const DIFFICULTY_LABEL: Record<Difficulty, string> = {
  easy: 'Easy', steady: 'Steady', hard: 'Hard', epic: 'Epic',
};
export const DIFFICULTY_COLOR: Record<Difficulty, string> = {
  easy: '#7bc86b', steady: '#56c6e8', hard: '#f4c04e', epic: '#b56bff',
};

export function rewardWeight(q: QuestRow): number {
  return q.quest.rewardSoft + q.quest.rewardXp * 5;
}

export function difficulty(q: QuestRow): Difficulty {
  const w = rewardWeight(q);
  if (w >= 1400) return 'epic';   // the 100-mob / 150-ore weeklies
  if (w >= 600) return 'hard';    // the rest of the weeklies
  if (w >= 200) return 'steady';  // the heavier dailies
  return 'easy';
}

// ─── Icons ──────────────────────────────────────────────────────────────────
/**
 * The `action` string the quest tracks is the most honest picture of what it asks
 * for — it is the same key game code fires through `trackAction`. Unknown actions
 * fall back to the scroll-ish default rather than rendering nothing.
 */
const ACTION_ICON: Record<string, IconName> = {
  harvest_crop: 'leaf',
  plant_crop: 'seed',
  arena_play: 'swords',
  arena_win: 'trophy',
  chop_wood: 'axe',
  mine_ore: 'pickaxe',
  gather_prop: 'chest',
  kill_mob: 'sword',
};

export function questIcon(q: QuestRow): IconName {
  return ACTION_ICON[q.quest.action ?? ''] ?? 'star';
}
